/**
 * AdminFinanceiro.js - Painel Financeiro do Admin
 * Gestão de faturamento, faturas e controle de uso
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DollarSign,
  TrendingUp,
  Building2,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Plus,
  RefreshCw,
  Loader2,
  ArrowLeft,
  Download,
  Filter,
  Calendar,
  Receipt
} from 'lucide-react';
import { toast } from 'sonner';

// Status das faturas
const INVOICE_STATUS = {
  pending: { label: 'Pendente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  paid: { label: 'Pago', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  overdue: { label: 'Vencida', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  cancelled: { label: 'Cancelada', color: 'bg-slate-100 text-slate-700', icon: XCircle }
};

// Formatação de moeda
const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

// Formatação de data
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR');
};

export default function AdminFinanceiro() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('resumo');
  
  // Dados
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  
  // Filtros
  const [selectedPeriod, setSelectedPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modal de nova fatura
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    company_id: '',
    period: selectedPeriod,
    amount: '',
    description: '',
    due_date: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedPeriod]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [summaryRes, invoicesRes, companiesRes] = await Promise.all([
        adminAPI.getBillingSummary(selectedPeriod),
        adminAPI.listInvoices(),
        adminAPI.getCompanies()
      ]);
      
      setSummary(summaryRes.data);
      setInvoices(invoicesRes.data);
      setCompanies(companiesRes.data.companies || companiesRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };

  // Criar fatura
  const handleCreateInvoice = async () => {
    if (!invoiceForm.company_id || !invoiceForm.amount) {
      toast.error('Preencha empresa e valor');
      return;
    }
    
    setCreating(true);
    try {
      await adminAPI.createInvoice({
        company_id: invoiceForm.company_id,
        period: invoiceForm.period || selectedPeriod,
        amount: parseFloat(invoiceForm.amount),
        description: invoiceForm.description,
        due_date: invoiceForm.due_date || undefined
      });
      
      toast.success('Fatura criada com sucesso');
      setShowInvoiceModal(false);
      setInvoiceForm({ company_id: '', period: selectedPeriod, amount: '', description: '', due_date: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar fatura');
    } finally {
      setCreating(false);
    }
  };

  // Atualizar status da fatura
  const handleUpdateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      await adminAPI.updateInvoiceStatus(invoiceId, newStatus);
      toast.success('Status atualizado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  // Gerar períodos (últimos 12 meses)
  const getPeriods = () => {
    const periods = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      periods.push(date.toISOString().slice(0, 7));
    }
    return periods;
  };

  // Filtrar faturas
  const filteredInvoices = invoices.filter(inv => {
    if (filterStatus !== 'all' && inv.status !== filterStatus) return false;
    return true;
  });

  // Calcular estatísticas
  const stats = {
    totalFaturado: summary?.total_geral || 0,
    totalEmpresas: summary?.total_empresas || 0,
    faturasPendentes: invoices.filter(i => i.status === 'pending').length,
    faturasVencidas: invoices.filter(i => i.status === 'overdue').length,
    totalRecebido: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.amount || 0), 0)
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Admin
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <DollarSign className="w-7 h-7 text-green-600" />
                  Financeiro
                </h1>
                <p className="text-slate-500">Gestão de faturamento e cobranças</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="w-[180px]">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getPeriods().map(p => (
                    <SelectItem key={p} value={p}>
                      {new Date(p + '-01').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Atualizar
              </Button>
              
              <Button onClick={() => setShowInvoiceModal(true)} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Nova Fatura
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Faturamento Período</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.totalFaturado)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Empresas Ativas</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.totalEmpresas}</p>
                </div>
                <Building2 className="w-8 h-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Faturas Pendentes</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.faturasPendentes}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Faturas Vencidas</p>
                  <p className="text-2xl font-bold text-red-600">{stats.faturasVencidas}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-red-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Total Recebido</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalRecebido)}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-300" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="resumo">Resumo por Empresa</TabsTrigger>
            <TabsTrigger value="faturas">Faturas</TabsTrigger>
          </TabsList>

          {/* Tab: Resumo por Empresa */}
          <TabsContent value="resumo" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Faturamento por Empresa - {new Date(selectedPeriod + '-01').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Plano</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead>Detalhes</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary?.empresas?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                          Nenhuma empresa com faturamento neste período
                        </TableCell>
                      </TableRow>
                    ) : (
                      summary?.empresas?.map((empresa) => (
                        <TableRow key={empresa.company_id}>
                          <TableCell className="font-medium">{empresa.company_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {empresa.plano_tipo === 'completo' ? 'Plano Completo' : 'Individual'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-bold text-green-600">
                            {formatCurrency(empresa.total)}
                          </TableCell>
                          <TableCell>
                            <div className="text-xs text-slate-500">
                              {empresa.details?.slice(0, 2).map((d, i) => (
                                <div key={i}>
                                  {d.feature}: {d.type === 'per_use' ? `${d.quantity}x` : ''} {formatCurrency(d.value)}
                                </div>
                              ))}
                              {empresa.details?.length > 2 && (
                                <div>+{empresa.details.length - 2} items</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setInvoiceForm({
                                  company_id: empresa.company_id,
                                  period: selectedPeriod,
                                  amount: empresa.total.toString(),
                                  description: `Fatura ${new Date(selectedPeriod + '-01').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`,
                                  due_date: ''
                                });
                                setShowInvoiceModal(true);
                              }}
                            >
                              <Receipt className="w-4 h-4 mr-1" />
                              Gerar Fatura
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Faturas */}
          <TabsContent value="faturas" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Todas as Faturas</CardTitle>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[150px]">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="pending">Pendentes</SelectItem>
                      <SelectItem value="paid">Pagas</SelectItem>
                      <SelectItem value="overdue">Vencidas</SelectItem>
                      <SelectItem value="cancelled">Canceladas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Período</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead>Vencimento</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                          Nenhuma fatura encontrada
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredInvoices.map((invoice) => {
                        const statusConfig = INVOICE_STATUS[invoice.status] || INVOICE_STATUS.pending;
                        const StatusIcon = statusConfig.icon;
                        
                        return (
                          <TableRow key={invoice.id}>
                            <TableCell className="font-medium">{invoice.company_name}</TableCell>
                            <TableCell>{invoice.period}</TableCell>
                            <TableCell className="text-right font-bold">
                              {formatCurrency(invoice.amount)}
                            </TableCell>
                            <TableCell>{formatDate(invoice.due_date)}</TableCell>
                            <TableCell>
                              <Badge className={statusConfig.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Select
                                value={invoice.status}
                                onValueChange={(val) => handleUpdateInvoiceStatus(invoice.id, val)}
                              >
                                <SelectTrigger className="w-[130px] h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pendente</SelectItem>
                                  <SelectItem value="paid">Pago</SelectItem>
                                  <SelectItem value="overdue">Vencida</SelectItem>
                                  <SelectItem value="cancelled">Cancelar</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal: Nova Fatura */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Fatura</DialogTitle>
            <DialogDescription>Criar fatura para empresa</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Empresa *</Label>
              <Select value={invoiceForm.company_id} onValueChange={(val) => setInvoiceForm({ ...invoiceForm, company_id: val })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecionar empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nome_fantasia || c.razao_social || c.nome || c.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Período</Label>
                <Input
                  type="month"
                  value={invoiceForm.period}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, period: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Valor *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={invoiceForm.amount}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })}
                  placeholder="0,00"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Vencimento</Label>
              <Input
                type="date"
                value={invoiceForm.due_date}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Descrição</Label>
              <Input
                value={invoiceForm.description}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
                placeholder="Descrição da fatura"
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateInvoice} disabled={creating} className="bg-green-600 hover:bg-green-700">
              {creating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Receipt className="w-4 h-4 mr-2" />
              )}
              Criar Fatura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
